import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronRight, 
  ArrowLeft, 
  UserPlus, 
  Save,
  AlertCircle,
  Users,
  Mail,
  Phone,
  User,
  Shield,
  Building,
  Tag,
  ChevronDown,
  Search,
  Check
} from "lucide-react";
import { useTeamManagement } from "../../../hooks/permissionHooks/useTeamManagement";
import { useCustomRoles } from "../../../hooks/permissionHooks/useCustomRoles";
import { useTeamValidation } from "../../../hooks/permissionHooks/useTeamValidation";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";

const RoleDropdown = ({ 
  roles, 
  selectedRole, 
  onSelect, 
  error, 
  touched 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const filteredRoles = roles.filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const selectedRoleData = roles.find(role => role.name === selectedRole);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        type="button"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-between ${
          error && touched 
            ? 'border-red-300 bg-red-50' 
            : isOpen 
              ? 'border-[#FF6C6C] bg-[#FF6C6C]/5' 
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <div className="flex-1">
          {selectedRoleData ? (
            <div>
              <h3 className="font-medium text-gray-800">{selectedRoleData.name}</h3>
              {/* <p className="text-sm text-gray-500 truncate">{selectedRoleData.description || 'No description available'}</p> */}
              <div className="flex gap-4 items-center mt-1 text-xs text-gray-400">
                <span>{Object.keys(selectedRoleData.permissions || {}).length} modules</span>
                <span>{Object.values(selectedRoleData.permissions || {}).flat().length} permissions</span>
              </div>
            </div>
          ) : (
            <div className="text-gray-500">
              <span>Select a role for this team member</span>
            </div>
          )}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="overflow-hidden absolute z-50 mt-2 w-full max-h-80 bg-white rounded-xl border border-gray-200 shadow-xl"
          >
            {/* Search - Only show if there are many roles */}
            {roles.length > 5 && (
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
                  />
                </div>
              </div>
            )}

            {/* Roles List */}
            <div className={`overflow-y-auto ${roles.length > 8 ? 'max-h-64' : 'max-h-80'}`}>
              {filteredRoles.length > 0 ? (
                filteredRoles.map((role) => {
                  const isSelected = selectedRole === role.name;
                  const totalPermissions = Object.values(role.permissions || {}).flat().length;
                  
                  return (
                    <motion.button
                      key={role.id}
                      type="button"
                      whileHover={{ backgroundColor: "#f9fafb" }}
                      onClick={() => {
                        onSelect(role.name);
                        setIsOpen(false);
                        setSearchTerm("");
                      }}
                      className={`w-full p-2.5 text-left transition-all duration-200 flex items-center justify-between hover:bg-gray-50 ${
                        isSelected 
                          ? 'bg-[#FF6C6C]/5 border-l-4 border-[#FF6C6C]' 
                          : ''
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h4 className={`font-medium truncate text-sm ${isSelected ? 'text-[#FF6C6C]' : 'text-gray-800'}`}>
                            {role.name}
                          </h4>
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 text-[#FF6C6C] flex-shrink-0 ml-2" />
                          )}
                        </div>
                        <div className="flex gap-3 items-center mt-0.5 text-xs text-gray-400">
                          <span>{Object.keys(role.permissions || {}).length} modules</span>
                          <span>{totalPermissions} permissions</span>
                        </div>
                      </div>
                    </motion.button>
                  );
                })
              ) : (
                <div className="p-4 text-center text-gray-500">
                  <p>No roles found matching "{searchTerm}"</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && touched && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-2 text-sm text-red-600"
        >
          {error}
        </motion.p>
      )}
    </div>
  );
};

const CreateTeamMember = () => {
  const navigate = useNavigate();
  const { user } = useSelector(state => state.user);
  const { createTeamMember, isCreating, canCreateMembers, requiresRoles, formatPhoneForApi } = useTeamManagement();
  const { customRoles, hasRoles } = useCustomRoles();
  const { validateTeamMemberData, canCreateTeamMembers } = useTeamValidation();
  const { canCreateRoles } = usePermissionCheck();

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: '',
    roleSlug: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Check if can create
  const { canCreate, reason: cannotCreateReason } = canCreateTeamMembers();

  // Handle input changes
  const handleInputChange = (field, value) => {
    let processedValue = value;
    
    // Special handling for phone number
    if (field === 'phone') {
      // Remove any non-digit characters except spaces
      processedValue = value.replace(/[^\d\s]/g, '');
      
      // Limit length based on prefix
      if (processedValue.startsWith('0')) {
        // Allow max 10 digits for numbers starting with 0
        processedValue = processedValue.substring(0, 10);
      } else if (processedValue.startsWith('233')) {
        // Allow max 12 digits for numbers starting with 233
        processedValue = processedValue.substring(0, 12);
      } else if (processedValue.length > 0) {
        // For other cases, limit to 12 digits max
        processedValue = processedValue.substring(0, 12);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle role selection
  const handleRoleSelect = (roleName) => {
    // Find the selected role to get its slug
    const selectedRole = customRoles.find(role => role.name === roleName);
    
    setFormData(prev => ({
      ...prev,
      role: roleName,
      roleSlug: selectedRole?.role_slug || ''
    }));

    if (errors.role) {
      setErrors(prev => ({ ...prev, role: null }));
    }
  };

  // Validation
  const validateForm = () => {
    const validation = validateTeamMemberData(formData);
    setErrors(validation.errors);
    return validation.isValid;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    if (!canCreate) {
      toast.error(cannotCreateReason);
      return;
    }

    try {
      // Format phone number for API
      const formattedData = {
        ...formData,
        phone: formatPhoneForApi(formData.phone)
      };
      
      createTeamMember(formattedData, {
        onSuccess: () => {
          toast.success("Team member created successfully!");
          navigate('/roles-and-permissions');
        },
        onError: (error) => {
          toast.error(error.message || "Failed to create team member");
        }
      });
    } catch (error) {
      toast.error("Failed to create team member");
    }
  };

  // Handle field blur
  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  // Check if user has permission to create team members
  if (!canCreateRoles()) {
    return (
      <div className="bg-white">
        <Navbar page={"Create Team Member"} />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 w-16 h-16 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-800">Access Restricted</h2>
            <p className="text-gray-500">You don't have permission to create team members.</p>
          </div>
        </div>
      </div>
    );
  }

  // If no roles available
  if (requiresRoles || !hasRoles) {
    return (
      <div className="bg-white">
        <Navbar page={"Create Team Member"} />
        <div className="p-6 min-h-screen bg-gray-50">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="max-w-md text-center">
              <div className="inline-block p-4 mb-4 bg-amber-100 rounded-full">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="mb-2 text-xl font-semibold text-gray-800">No Roles Available</h2>
              <p className="mb-6 text-gray-500">
                You need to create at least one role before you can add team members.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/roles/create')}
                className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium"
              >
                Create Your First Role
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Navbar page={"Create Team Member"} />
      
      <div className="p-6 min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex gap-2 items-center mb-6 text-sm text-gray-500"
        >
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <ChevronRight size={16} />
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/roles-and-permissions')}
          >
            Roles & Permissions
          </span>
          <ChevronRight size={16} />
          <span className="text-[#FF6C6C] font-medium">Create Team Member</span>
        </motion.div>

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex gap-4 items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/roles-and-permissions')}
              className="p-2 text-gray-400 rounded-xl transition-all duration-200 hover:text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>
            <div className="flex gap-3 items-center">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 via-pink-50 to-white">
                <UserPlus className="w-6 h-6 text-[#FF6C6C]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Add Team Member</h1>
                <p className="text-sm text-gray-500">Invite a new member to your team</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSubmit}
          className="mx-auto max-w-7xl"
        >
          <div className="space-y-8">
            {/* Brand Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center mb-6">
                <div className="p-2 bg-[#FF6C6C]/10 rounded-lg">
                  <Building className="w-5 h-5 text-[#FF6C6C]" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Brand Information</h2>
                  <p className="text-sm text-gray-500">Adding team member to your brand</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="p-4 bg-gradient-to-br from-[#FF6C6C]/5 to-pink-50 rounded-xl border border-[#FF6C6C]/20">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 bg-[#FF6C6C]/20 rounded-lg">
                      <Building className="w-4 h-4 text-[#FF6C6C]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Brand Name</p>
                      <p className="text-lg font-semibold text-gray-800">{user?.brand_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                  <div className="flex gap-3 items-center">
                    <div className="p-2 bg-purple-200 rounded-lg">
                      <Tag className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-600">Category</p>
                      <p className="text-lg font-semibold text-gray-800">{user?.brand_category || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center mb-6">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Personal Information</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleInputChange('fullName', e.target.value)}
                    onBlur={() => handleBlur('fullName')}
                    placeholder="Enter full name"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                      errors.fullName && touched.fullName ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                    }`}
                  />
                  {errors.fullName && touched.fullName && (
                    <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      placeholder="Enter email address"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                        errors.email && touched.email ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                      }`}
                    />
                  </div>
                  {errors.email && touched.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      placeholder="0XXXXXXXXX or 233XXXXXXXXX"
                      className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] ${
                        errors.phone && touched.phone ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
                      }`}
                    />
                  </div>
                  {errors.phone && touched.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                  {!errors.phone && (
                    <p className="mt-1 text-xs text-gray-500">
                      Format: Start with 0 (10 digits) or 233 (12 digits)
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Role Assignment */}
            <div className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="flex gap-3 items-center mb-6">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Shield className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Role Assignment</h2>
                  <p className="text-sm text-gray-500">Select a role for this team member</p>
                </div>
              </div>

              <RoleDropdown
                roles={customRoles}
                selectedRole={formData.role}
                onSelect={handleRoleSelect}
                error={errors.role}
                touched={touched.role}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 justify-end items-center p-6 mt-8 bg-white rounded-xl border border-gray-100 shadow-sm">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles-and-permissions')}
              className="px-6 py-3 font-medium text-gray-600 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
            >
              Cancel
            </motion.button>
            
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isCreating || !canCreate}
              className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={!canCreate ? cannotCreateReason : ''}
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Create Team Member
                </>
              )}
            </motion.button>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default CreateTeamMember; 